import React, { useCallback, useEffect, useRef, useState } from 'react';
import { attachOlderBarLoader, captureVisibleRange, restoreVisibleRange } from '@/components/modern/chartPaging';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import {
  FOREX_CHART_PERIODS,
  fmtForexSymbol,
  type ForexService,
  type FxcmCandle,
  type FxcmQuote,
  type FxcmPosition,
} from '@/services/forexService';

// Seconds (UTC epoch) — lightweight-charts' time unit for an intraday series.
type Bar = { time: number; open: number; high: number; low: number; close: number };

const toBar = (c: FxcmCandle): Bar => ({
  time: Math.floor(new Date(c.timestampUtc).getTime() / 1000),
  open: c.open, high: c.high, low: c.low, close: c.close,
});

// Period code → bar length in seconds, for rolling a fresh candle on live ticks.
const PERIOD_SECONDS: Record<string, number> = {
  m1: 60, m5: 300, m15: 900, m30: 1800, H1: 3600, H4: 14400, D1: 86400, W1: 604800,
};

// A quote older than this is treated as stale (broker stream stalled) and must not
// keep stretching the live candle with a frozen value.
const STALE_QUOTE_MS = 15_000;

// We only roll a fresh candle locally for timeframes whose buckets align to BOTH the
// Unix epoch and FXCM's session — i.e. ≤ H1 (top-of-hour). H4 blocks are session-
// offset and D1/W1 start at the session boundary, so for those we extend the current
// bar and let the refetch supply the correctly-aligned next candle.
const LOCAL_ROLL_MAX_SECONDS = 3_600;

function themeOptions(isDark: boolean) {
  return {
    layout: { background: { color: 'transparent' }, textColor: isDark ? '#d1d4dc' : '#333' },
    grid: {
      vertLines: { color: isDark ? 'rgba(197,203,206,0.1)' : 'rgba(197,203,206,0.2)' },
      horzLines: { color: isDark ? 'rgba(197,203,206,0.1)' : 'rgba(197,203,206,0.2)' },
    },
  };
}

/**
 * Inline candlestick chart for the instrument the panel is focused on. Candles come
 * from FXCM's market-data snapshot (mid OHLC) via /candles; the right-most candle is
 * nudged live from the quote tick (rolling a fresh bar at the period boundary), and a
 * periodic refetch swaps in completed bars WITHOUT rebuilding the chart (so pan/zoom
 * survive). An open position's entry / SL / TP are drawn as price lines.
 */
const INITIAL_CANDLES = 200;
/** Each pan-back asks FXCM for this many more candles, up to MAX_CANDLES. */
const CANDLES_PER_PAGE = 400;
const MAX_CANDLES = 5000;

export function ForexChart({
  svc, symbol, quote, positions,
}: {
  svc: ForexService;
  symbol: string;
  quote?: FxcmQuote | null;
  positions?: FxcmPosition[];   // all open trades on this symbol (hedged book → many)
}) {
  const [period, setPeriod] = useState('H1');
  // FXCM's /candles takes a count, not a date range, so paging back means asking
  // for a bigger tail. Grows on pan-back; resets whenever symbol/period changes.
  const [count, setCount] = useState(INITIAL_CANDLES);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bars, setBars] = useState<Bar[]>([]);
  const [ready, setReady] = useState(false);   // chart built + series created

  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<any>(null);
  const seriesRef = useRef<any>(null);
  const lastBarRef = useRef<Bar | null>(null);
  const priceLinesRef = useRef<any[]>([]);
  const fitPendingRef = useRef(true);   // refit on first data + symbol/period change
  const reqSeqRef = useRef(0);          // guards against out-of-order /candles responses

  const isJpy = symbol.toUpperCase().includes('JPY');
  const precision = isJpy ? 3 : 5;
  const minMove = isJpy ? 0.001 : 0.00001;

  // ── Data load (symbol/period) + slow refetch, paused when tab hidden ──
  const loadCandles = useCallback(async () => {
    const seq = ++reqSeqRef.current;   // newest request wins
    setLoading(true); setError(null);
    try {
      const data = await svc.getCandles(symbol, period, count);
      if (seq !== reqSeqRef.current) return;   // superseded by a newer symbol/period
      setBars(data.map(toBar));
    } catch (e) {
      if (seq !== reqSeqRef.current) return;
      // Keep the last-good bars on a refetch failure (e.g. a transient "session busy"
      // skip) — bars were already cleared on the symbol/period switch, so this only
      // preserves same-symbol history rather than blanking the chart on a blip.
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      if (seq === reqSeqRef.current) setLoading(false);
    }
  }, [svc, symbol, period, count]);

  // Series RESET — only when the instrument or timeframe actually changes.
  //
  // This used to live in the load effect below, which depends on `loadCandles`, which depends on
  // `count`. Pan-back raising `count` therefore rebuilt `loadCandles`, reran the effect, and the
  // effect reset `count` straight back to INITIAL_CANDLES — then the 200-candle request superseded
  // the expanded one via reqSeqRef. Paging could never retain the older candles it had just asked
  // for. Keyed on [symbol, period], a count-driven reload no longer trips the reset.
  useEffect(() => {
    fitPendingRef.current = true;   // new instrument / timeframe → refit
    setCount(INITIAL_CANDLES);      // start each series from a fresh tail
    lastBarRef.current = null;      // don't let a tick stretch the previous symbol's bar
    setBars([]);                    // drop the previous instrument's bars (a failed
                                    // fetch then leaves the chart empty, not stale)
    // Clear the canvas immediately so we never show the PREVIOUS instrument's candles
    // during the (usually brief) fetch of the new one.
    try { seriesRef.current?.setData([]); } catch { /* not built yet */ }
  }, [symbol, period]);

  // LOAD — reruns whenever the request identity changes, INCLUDING count, so pan-back actually
  // fetches the wider window.
  useEffect(() => {
    void loadCandles();
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') void loadCandles();
    }, 60_000);
    return () => clearInterval(id);
  }, [loadCandles]);

  // ── Build the chart ONCE; tear down on unmount ──
  useEffect(() => {
    if (!containerRef.current) return;
    let cancelled = false;
    let observer: MutationObserver | null = null;
    (async () => {
      const lc = await import('lightweight-charts');
      const { createChart, CandlestickSeries } = lc as any;
      if (cancelled || !containerRef.current) return;

      const isDark = document.documentElement.classList.contains('dark');
      const chart = createChart(containerRef.current, {
        autoSize: true,
        ...themeOptions(isDark),
        timeScale: { timeVisible: true, secondsVisible: false, borderColor: isDark ? 'rgba(197,203,206,0.3)' : '#ccc' },
        rightPriceScale: { borderColor: isDark ? 'rgba(197,203,206,0.3)' : '#ccc' },
        crosshair: { mode: 0 },
      });
      const series = chart.addSeries(CandlestickSeries, {
        upColor: '#26a69a', downColor: '#ef5350',
        borderUpColor: '#26a69a', borderDownColor: '#ef5350',
        wickUpColor: '#26a69a', wickDownColor: '#ef5350',
      });
      chartRef.current = chart;
      seriesRef.current = series;

      // Re-apply colours on light/dark toggle (chart is built once, so otherwise
      // the theme would stay frozen at build time).
      observer = new MutationObserver(() => {
        try { chart.applyOptions(themeOptions(document.documentElement.classList.contains('dark'))); }
        catch { /* chart disposed */ }
      });
      observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });

      setReady(true);
    })();
    return () => {
      cancelled = true;
      observer?.disconnect();
      if (chartRef.current) { try { chartRef.current.remove(); } catch { /* gone */ } }
      chartRef.current = null; seriesRef.current = null; lastBarRef.current = null;
      priceLinesRef.current = [];
      setReady(false);
    };
  }, []);

  // ── Price precision follows the symbol (JPY = 3dp) ──
  useEffect(() => {
    if (!ready || !seriesRef.current) return;
    seriesRef.current.applyOptions({ priceFormat: { type: 'price', precision, minMove } });
  }, [ready, precision, minMove]);

  // ── Swap candle data without rebuilding (preserves pan/zoom). Empty bars CLEAR the
  //    series (a failed fetch / no-data symbol must not keep showing the old one, and
  //    must null lastBar so a tick can't stretch a stale candle). ──
  const firstBarTimeRef = useRef<number | null>(null);

  useEffect(() => {
    if (!ready || !seriesRef.current) return;
    // A pan-back page arrives as a longer array with an OLDER first candle.
    // setData re-indexes everything, so hold the viewport by time across it —
    // otherwise every page load yanks the user back to the right edge.
    const newFirst = bars.length ? Number(bars[0].time) : null;
    const isPrepend = newFirst != null && firstBarTimeRef.current != null
      && newFirst < firstBarTimeRef.current;
    const keep = isPrepend ? captureVisibleRange(chartRef.current) : null;

    seriesRef.current.setData(bars);
    firstBarTimeRef.current = newFirst;
    lastBarRef.current = bars.length ? bars[bars.length - 1] : null;

    if (isPrepend) {
      restoreVisibleRange(chartRef.current, keep);
    } else if (bars.length && fitPendingRef.current) {
      try { chartRef.current?.timeScale().fitContent(); } catch { /* gone */ }
      fitPendingRef.current = false;
    }
  }, [ready, bars]);

  // ── Pan-back paging ──
  const pageStateRef = useRef({ count: INITIAL_CANDLES, loaded: 0 });
  pageStateRef.current = { count, loaded: bars.length };

  useEffect(() => {
    if (!ready || !chartRef.current) return;
    const handle = attachOlderBarLoader(chartRef.current, {
      onLoadOlder: async () => {
        const { count: cur, loaded } = pageStateRef.current;
        // Already asked for more than the broker returned → that's all there is.
        if (loaded < cur || cur >= MAX_CANDLES) return 0;
        setCount(Math.min(cur + CANDLES_PER_PAGE, MAX_CANDLES));
        // The refetch lands through the effect above. Report a non-zero count so
        // the loader stays armed; the `loaded < cur` check above is what
        // eventually latches it off.
        return CANDLES_PER_PAGE;
      },
    });
    return () => handle.detach();
  }, [ready, symbol, period]);

  // ── Open-position overlay (entry / SL / TP per trade) — own effect so editing SL/TP
  //    doesn't rebuild the chart. Draws lines for EVERY trade on the symbol (a hedged
  //    book can hold several). Keyed by a content signature so it only redraws on a
  //    real change, not on every parent re-render (quote ticks). ──
  // Signature for change-detection. Separators ':' and '|' are safe — trade ids are
  // numeric and rates are decimals, neither contains them.
  const posSig = (positions ?? [])
    .map(p => `${p.tradeId}:${p.openRate}:${p.stop ?? ''}:${p.limit ?? ''}:${p.side}`).join('|');
  const multiPos = (positions ?? []).length > 1;
  useEffect(() => {
    const series = seriesRef.current;
    if (!ready || !series) return;
    for (const line of priceLinesRef.current) { try { series.removePriceLine(line); } catch { /* gone */ } }
    priceLinesRef.current = [];
    const add = (price: number, color: string, title: string, dashed = false) =>
      priceLinesRef.current.push(series.createPriceLine({
        price, color, lineWidth: 1, lineStyle: dashed ? 2 : 0, axisLabelVisible: true, title,
      }));
    for (const p of positions ?? []) {
      // On a hedged book, suffix the trade id so same-direction entries are distinct.
      const tag = multiPos ? ` #${p.tradeId.slice(-4)}` : '';
      add(p.openRate, '#58a6ff', `entry ${p.side}${tag}`);
      if (p.stop != null) add(p.stop, '#ff9800', `SL${tag}`, true);
      if (p.limit != null) add(p.limit, '#4caf50', `TP${tag}`, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, posSig]);

  // ── Live tick: extend the current candle, or roll a fresh one at the boundary ──
  useEffect(() => {
    const series = seriesRef.current;
    const last = lastBarRef.current;
    if (!ready || !series || !last || !quote) return;
    if (Date.now() - new Date(quote.timestampUtc).getTime() > STALE_QUOTE_MS) return;   // zombie tick
    const mid = (quote.bid + quote.ask) / 2;
    if (!(mid > 0)) return;

    const sec = PERIOD_SECONDS[period] ?? 3600;
    // Roll a fresh bar locally only where an epoch-aligned bucket matches FXCM's candle
    // boundary (≤ H1). H4 is session-offset and D1/W1 start on the session boundary
    // (~22:00 UTC daily, Sunday open weekly), so for those we extend the current bar
    // and let the 60s refetch supply the correctly-aligned next candle.
    const canRoll = sec <= LOCAL_ROLL_MAX_SECONDS;
    const bucket = Math.floor(new Date(quote.timestampUtc).getTime() / 1000 / sec) * sec;
    const bar: Bar = canRoll && bucket > last.time
      ? { time: bucket, open: mid, high: mid, low: mid, close: mid }            // new candle
      : { time: last.time, open: last.open, high: Math.max(last.high, mid), low: Math.min(last.low, mid), close: mid };
    lastBarRef.current = bar;
    try { series.update(bar); } catch { /* series disposed mid-swap */ }
  }, [quote, ready, period]);

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1 flex-wrap">
        <span className="text-sm font-medium mr-2">{fmtForexSymbol(symbol)}</span>
        {FOREX_CHART_PERIODS.map(p => (
          <Button
            key={p.code}
            size="sm"
            variant={period === p.code ? 'default' : 'outline'}
            className="h-6 px-2 text-xs"
            onClick={() => setPeriod(p.code)}
          >
            {p.label}
          </Button>
        ))}
        {loading && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
      </div>
      {/* Only surface an error when we have nothing to show — a transient refetch blip
          while last-good candles are still on screen stays silent. */}
      {error && bars.length === 0 && (
        <div className="text-xs text-destructive">Chart unavailable: {error}</div>
      )}
      <div ref={containerRef} className="w-full" style={{ height: 320 }} />
      {!loading && !error && bars.length === 0 && (
        <div className="text-xs text-muted-foreground">No candle data for {fmtForexSymbol(symbol)}.</div>
      )}
    </div>
  );
}
